import React, { useEffect, useMemo, useState } from 'react';
import {
  LayersControl,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import { FaCrosshairs, FaMapMarkerAlt, FaRedo, FaSave, FaTimes, FaUndo } from 'react-icons/fa';
import 'leaflet/dist/leaflet.css';
import { leafletParaGeoJson } from '../sensoriamento/stacService';
import { calcularAreaHectares, formatarAreaHectares } from '../sensoriamento/territorialGeometry';
import { validarGeometriaTalhao } from '../sensoriamento/territorialSnapping';

const BRAZIL_CENTER = [-15.78, -47.93];

const vertexIcon = L.divIcon({
  className: 'migration-perimeter-vertex',
  html: '<span></span>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const geometryPositions = geometry => geometry?.geometry?.type === 'Polygon'
  ? geometry.geometry.coordinates?.[0]
    ?.slice(0, -1)
    .map(([lng, lat]) => [Number(lat), Number(lng)])
    .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng)) || []
  : [];

function ClickCollector({ enabled, onAdd }) {
  useMapEvents({
    click(event) {
      if (!enabled) return;
      onAdd({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });
  return null;
}

function MapController({ bounds, target }) {
  const map = useMap();

  useEffect(() => {
    if (!bounds?.length) return;
    map.fitBounds(bounds, { padding: [38, 38], maxZoom: 17, animate: false });
  }, [bounds, map]);

  useEffect(() => {
    if (!target) return;
    map.setView([target.lat, target.lng], target.zoom || 17, { animate: false });
  }, [map, target]);

  return null;
}

export default function MigrationPerimeterEditor({
  property,
  plot,
  existingDraft,
  onClose,
  onSave,
  contextLabel = 'Cadastro local de perímetro',
  saveLabel = 'Salvar somente nesta revisão',
}) {
  const [points, setPoints] = useState(() => geometryPositions(existingDraft?.geometria)
    .map(([lat, lng]) => ({ lat, lng })));
  const [coordinateSearch, setCoordinateSearch] = useState('');
  const [mapTarget, setMapTarget] = useState(null);
  const [locationError, setLocationError] = useState('');

  const propertyPositions = useMemo(() => geometryPositions(property?.geometria), [property]);
  const siblingPolygons = useMemo(() => (property?.talhoes || [])
    .filter(item => String(item.id) !== String(plot.id) && item.geometria)
    .map(item => ({ ...item, positions: geometryPositions(item.geometria) }))
    .filter(item => item.positions.length >= 3), [property, plot.id]);
  const allReferencePositions = useMemo(() => [
    ...propertyPositions,
    ...siblingPolygons.flatMap(item => item.positions),
  ], [propertyPositions, siblingPolygons]);

  const initialCenter = useMemo(() => {
    if (propertyPositions.length) return propertyPositions[0];
    if (allReferencePositions.length) return allReferencePositions[0];
    const lat = Number(property?.coordenadaY);
    const lng = Number(property?.coordenadaX);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    return BRAZIL_CENTER;
  }, [allReferencePositions, property, propertyPositions]);

  const draftGeometry = points.length >= 3 ? leafletParaGeoJson(points) : null;
  const area = draftGeometry ? calcularAreaHectares(draftGeometry) : null;
  const validation = useMemo(() => {
    if (!draftGeometry) return { valido: false, mensagem: 'Marque pelo menos três pontos no mapa.' };
    return validarGeometriaTalhao({
      geometria: draftGeometry,
      propriedade: property?.geometria || null,
      talhoesExistentes: siblingPolygons.map(item => ({ id: item.id, nome: item.nome, geometria: item.geometria })),
    });
  }, [draftGeometry, property, siblingPolygons]);

  const moveVertex = (index, latlng) => {
    setPoints(current => current.map((point, pointIndex) => pointIndex === index
      ? { lat: latlng.lat, lng: latlng.lng }
      : point));
  };

  const removeVertex = index => {
    setPoints(current => current.filter((_, pointIndex) => pointIndex !== index));
  };

  const centralizeCoordinates = () => {
    const match = coordinateSearch.trim().match(/^(-?\d{1,2}(?:[.,]\d+)?)\s*[,;]\s*(-?\d{1,3}(?:[.,]\d+)?)$/);
    if (!match) {
      setLocationError('Digite latitude e longitude, por exemplo: -19.4500, -44.6400');
      return;
    }
    const lat = Number(match[1].replace(',', '.'));
    const lng = Number(match[2].replace(',', '.'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      setLocationError('As coordenadas informadas não são válidas.');
      return;
    }
    setLocationError('');
    setMapTarget({ lat, lng, zoom: 17 });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Este navegador não disponibilizou a localização atual.');
      return;
    }
    setLocationError('Localizando…');
    navigator.geolocation.getCurrentPosition(
      position => {
        setLocationError('');
        setMapTarget({ lat: position.coords.latitude, lng: position.coords.longitude, zoom: 18 });
      },
      () => setLocationError('Não foi possível acessar sua localização. Você pode digitar as coordenadas.'),
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const handleSave = () => {
    if (!draftGeometry || !validation.valido) return;
    onSave({
      propriedadeId: property.id,
      propriedadeNome: property.nome,
      talhaoFisicoId: plot.id,
      talhaoLegadoId: plot.legado.id,
      talhaoNome: plot.nome,
      geometria: draftGeometry,
      areaCalculada: area,
      validacao: {
        valido: true,
        propriedadePossuiGeometria: Boolean(property.geometria),
        talhoesVizinhosComparados: siblingPolygons.length,
      },
      atualizadoEm: new Date().toISOString(),
    });
  };

  return (
    <div className="migration-perimeter-overlay" role="dialog" aria-modal="true" aria-label={`Cadastrar perímetro de ${plot.nome}`}>
      <section className="migration-perimeter-editor">
        <header>
          <div>
            <span>{contextLabel}</span>
            <h2>{plot.nome}</h2>
            <p>{property.nome} · clique nos cantos da área e arraste os pontos para ajustar.</p>
          </div>
          <button type="button" className="migration-perimeter-close" aria-label="Fechar editor" onClick={onClose}><FaTimes /></button>
        </header>

        <div className="migration-perimeter-toolbar">
          <div className="migration-coordinate-search">
            <input
              type="text"
              value={coordinateSearch}
              placeholder="Latitude, longitude"
              aria-label="Latitude e longitude para centralizar"
              onChange={event => setCoordinateSearch(event.target.value)}
              onKeyDown={event => { if (event.key === 'Enter') centralizeCoordinates(); }}
            />
            <button type="button" onClick={centralizeCoordinates}><FaMapMarkerAlt /> Ir</button>
            <button type="button" onClick={useCurrentLocation}><FaCrosshairs /> Minha localização</button>
          </div>
          <div className="migration-drawing-actions">
            <button type="button" disabled={points.length === 0} onClick={() => setPoints(current => current.slice(0, -1))}><FaUndo /> Desfazer ponto</button>
            <button type="button" disabled={points.length === 0} onClick={() => setPoints([])}><FaRedo /> Recomeçar</button>
          </div>
        </div>
        {locationError && <div className="migration-location-message">{locationError}</div>}

        <div className="migration-perimeter-map">
          <MapContainer center={initialCenter} zoom={initialCenter === BRAZIL_CENTER ? 5 : 15} zoomAnimation={false} fadeAnimation={false}>
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Satélite">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="Esri World Imagery"
                  maxZoom={20}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Mapa">
                <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" maxZoom={20} />
              </LayersControl.BaseLayer>
            </LayersControl>

            <MapController bounds={allReferencePositions} target={mapTarget} />
            <ClickCollector enabled onAdd={point => setPoints(current => [...current, point])} />

            {propertyPositions.length >= 3 && (
              <Polygon
                positions={propertyPositions}
                pathOptions={{ color: '#f0a128', weight: 3, dashArray: '9,7', fillOpacity: 0.025 }}
                interactive={false}
              >
                <Tooltip sticky>Limite da propriedade</Tooltip>
              </Polygon>
            )}

            {siblingPolygons.map(item => (
              <Polygon
                key={item.id}
                positions={item.positions}
                pathOptions={{ color: '#2f84b9', weight: 2, fillColor: '#4ba3d5', fillOpacity: 0.12 }}
                interactive={false}
              >
                <Tooltip sticky>Talhão existente · {item.nome}</Tooltip>
              </Polygon>
            ))}

            {points.length >= 3 ? (
              <Polygon
                positions={points.map(point => [point.lat, point.lng])}
                pathOptions={validation.valido
                  ? { color: '#12a27e', weight: 3, fillColor: '#19b58d', fillOpacity: 0.24 }
                  : { color: '#d95143', weight: 3, fillColor: '#e05a4b', fillOpacity: 0.2 }}
              >
                {area && <Tooltip permanent direction="center">{formatarAreaHectares(area)}</Tooltip>}
              </Polygon>
            ) : points.length > 0 ? (
              <Polyline positions={points.map(point => [point.lat, point.lng])} pathOptions={{ color: '#ff6d00', weight: 3, dashArray: '6,4' }} />
            ) : null}

            {points.map((point, index) => (
              <Marker
                key={`${index}-${point.lat}-${point.lng}`}
                position={[point.lat, point.lng]}
                icon={vertexIcon}
                draggable
                eventHandlers={{
                  dragend: event => moveVertex(index, event.target.getLatLng()),
                  dblclick: event => {
                    L.DomEvent.stopPropagation(event.originalEvent);
                    removeVertex(index);
                  },
                  contextmenu: event => {
                    L.DomEvent.stopPropagation(event.originalEvent);
                    removeVertex(index);
                  },
                }}
              >
                <Tooltip direction="top">Ponto {index + 1} · arraste para ajustar · duplo clique para excluir</Tooltip>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <footer>
          <div className={`migration-perimeter-validation ${validation.valido ? 'is-valid' : 'is-invalid'}`}>
            <strong>{points.length} ponto(s) · {formatarAreaHectares(area)}</strong>
            <span>{validation.valido
              ? property.geometria
                ? 'Perímetro válido e dentro da propriedade, sem sobreposição com áreas existentes.'
                : 'Desenho válido. Como a propriedade não possui perímetro, confirme visualmente a localização.'
              : validation.mensagem}</span>
          </div>
          <div className="migration-perimeter-footer-actions">
            <button type="button" onClick={onClose}>Cancelar</button>
            <button type="button" className="is-primary" disabled={!validation.valido} onClick={handleSave}><FaSave /> {saveLabel}</button>
          </div>
        </footer>
      </section>
    </div>
  );
}
